import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Fingerprint } from 'lucide-react';
import { PERMISSIONS } from '@nova/shared';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { getAttendance, type Today } from './api';

/**
 * Title-bar punch reminder. Shown only when the API says this user must punch
 * (`attendanceRequired` already folds in HR enabled + active profile + policy).
 * Pulses while today has a shift and no accepted check-in yet.
 */
export function AttendancePunchIndicator() {
  const { t } = useTranslation('attendance');
  const user = useAuthStore((state) => state.user);
  const canAny = useAuthStore((state) => state.canAny);
  const allowed = Boolean(user && canAny(PERMISSIONS.ATTENDANCE_SELF));
  // Same key as MyAttendancePage, so a punch there refreshes this icon too.
  const state = useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: () => getAttendance<Today>('me/attendance'),
    enabled: allowed,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: false,
  });
  const data = state.data;
  if (!allowed || !data?.attendanceRequired) return null;

  const checkedInToday = !!data.openSession || !!data.checkedInToday;
  const pending = data.shifts.length > 0 && !checkedInToday;
  const label = t(pending ? 'indicator.notCheckedIn' : data.openSession ? 'checkedIn' : 'checkedOut');

  return (
    <Link
      to="/hr/me"
      data-testid="attendance-punch-indicator"
      data-pending={pending ? 'true' : 'false'}
      title={label}
      aria-label={label}
      className="relative flex h-7 w-7 items-center justify-center rounded-md text-slate-300 hover:bg-white/10 hover:text-white"
    >
      <Fingerprint className={cn('h-4 w-4', pending && 'text-amber-300')} aria-hidden="true" />
      {pending ? (
        <span className="absolute end-0.5 top-0.5 flex h-2 w-2" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
        </span>
      ) : null}
    </Link>
  );
}
